import dayjs from 'dayjs'
import 'dayjs/locale/zh-cn'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

// 配置dayjs插件
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.locale('zh-cn')

// 设置默认时区为北京时间
const BEIJING_TIMEZONE = 'Asia/Shanghai'

// 格式化日期时间 - 统一使用北京时间
export const formatDateTime = (date, format = 'YYYY-MM-DD HH:mm:ss') => {
  if (!date) return ''
  
  // 创建dayjs对象并设置为北京时区
  let dayjsObj = dayjs(date)
  
  // 如果日期没有时区信息，假设它是北京时间
  if (typeof date === 'string' && !date.includes('T') && !date.includes('+') && !date.includes('Z')) {
    // 对于 'YYYY-MM-DD HH:mm:ss' 格式的字符串，直接作为北京时间处理
    dayjsObj = dayjs.tz(date, BEIJING_TIMEZONE)
  } else {
    // 对于其他格式，转换为北京时区
    dayjsObj = dayjs(date).tz(BEIJING_TIMEZONE)
  }
  
  return dayjsObj.format(format)
}

// 格式化日期
export const formatDate = (date, format = 'YYYY-MM-DD') => {
  if (!date) return ''
  return formatDateTime(date, format)
}

// 格式化时间
export const formatTime = (date, format = 'HH:mm:ss') => {
  if (!date) return ''
  return formatDateTime(date, format)
}

// 格式化为相对时间（中文）
export const formatRelativeTime = (date) => {
  if (!date) return ''
  
  const dayjsObj = dayjs(date).tz(BEIJING_TIMEZONE)
  const now = dayjs().tz(BEIJING_TIMEZONE)
  
  const diffInMinutes = now.diff(dayjsObj, 'minute')
  const diffInHours = now.diff(dayjsObj, 'hour')
  const diffInDays = now.diff(dayjsObj, 'day')
  
  if (diffInMinutes < 1) {
    return '刚刚'
  } else if (diffInMinutes < 60) {
    return `${diffInMinutes}分钟前`
  } else if (diffInHours < 24) {
    return `${diffInHours}小时前`
  } else if (diffInDays < 7) {
    return `${diffInDays}天前`
  } else {
    return formatDateTime(date, 'MM-DD HH:mm')
  }
}

// 获取当前北京时间
export const getNow = () => {
  return dayjs().tz(BEIJING_TIMEZONE)
}

// 获取当前时间字符串 (格式: YYYY-MM-DD HH:mm:ss)
export const getCurrentTime = () => {
  return getNow().format('YYYY-MM-DD HH:mm:ss')
}

// 获取北京时间的时间戳
export const getTimestamp = () => {
  return getNow().valueOf()
}

// 判断是否是今天
export const isToday = (date) => {
  if (!date) return false
  const dateObj = dayjs(date).tz(BEIJING_TIMEZONE)
  const today = getNow()
  return dateObj.isSame(today, 'day')
}

// 判断是否是昨天
export const isYesterday = (date) => {
  if (!date) return false
  const dateObj = dayjs(date).tz(BEIJING_TIMEZONE)
  const yesterday = getNow().subtract(1, 'day')
  return dateObj.isSame(yesterday, 'day')
}

// 格式化租户角色文本
export const formatTenantRoleText = (role) => {
  const roleMap = {
    'ADMIN': '管理员',
    'NORMAL_USER': '普通用户',
    'SALES': '销售',
    'BD': 'BD'
  }
  return roleMap[role] || role || '未知角色'
}

// 格式化文件大小
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B'
  
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`
}

// 格式化数字
export const formatNumber = (num) => {
  if (num === null || num === undefined) return '--'
  return Number(num).toLocaleString('zh-CN')
}

// 格式化货币
export const formatCurrency = (amount, currency = '¥') => {
  if (amount === null || amount === undefined) return '--'
  return `${currency} ${Number(amount).toLocaleString('zh-CN', { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  })}`
}

// 格式化金额
export const formatAmount = (amount) => {
  if (!amount && amount !== 0) return ''
  return amount.toLocaleString('zh-CN', {
    style: 'currency',
    currency: 'CNY',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

// 格式化手机号
export const formatPhone = (phone) => {
  if (!phone) return ''
  return phone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
}

// 格式化身份证号
export const formatIdCard = (idCard) => {
  if (!idCard) return ''
  return idCard.replace(/(\d{6})\d{8}(\d{4})/, '$1********$2')
}

// 格式化银行卡号
export const formatBankCard = (cardNo) => {
  if (!cardNo) return ''
  return cardNo.replace(/(\d{4})\d+(\d{4})/, '$1 **** **** $2')
}

/**
 * 智能截断文本，保持单词完整性
 * @param {string} text - 要截断的文本
 * @param {number} maxLength - 最大字符长度
 * @param {string} suffix - 截断后缀，默认为 '...'
 * @returns {string} 截断后的文本
 * 
 * 算法说明：
 * 1. 如果截断点在中文、空格或标点，直接截断
 * 2. 如果截断点在英文单词中间，回退到最后一个单词边界
 * 3. 支持中英文混合文本的智能处理
 */
export const truncateText = (text, maxLength, suffix = '...') => {
  if (!text) return ''
  
  // 如果文本长度不超过限制，直接返回
  if (text.length <= maxLength) {
    return text
  }
  
  // 截取指定长度
  let truncated = text.substring(0, maxLength)
  
  // 检查截断点的字符类型
  const lastChar = truncated[truncated.length - 1]
  const nextChar = text[maxLength]
  
  // 判断是否在英文单词中间截断
  // 条件：当前字符和下一个字符都是字母或数字
  const isInEnglishWord = /[a-zA-Z0-9]/.test(lastChar) && /[a-zA-Z0-9]/.test(nextChar)
  
  if (isInEnglishWord) {
    // 在英文单词中间截断了，需要回退到单词边界
    // 从后往前查找最后一个非字母数字字符（空格、标点等）
    let lastBreakIndex = -1
    
    for (let i = truncated.length - 1; i >= 0; i--) {
      const char = truncated[i]
      // 找到分隔符（空格、标点、中文字符等）
      if (!/[a-zA-Z0-9]/.test(char)) {
        lastBreakIndex = i
        break
      }
    }
    
    if (lastBreakIndex > 0) {
      // 找到了单词边界，截断到该位置
      truncated = truncated.substring(0, lastBreakIndex + 1).trim()
    } else {
      // 没找到边界（整段都是一个长单词），保持原截断
      // 这种情况很少见，通常是超长的专业术语
      truncated = truncated.trim()
    }
  } else {
    // 截断点不在英文单词中间，直接截断即可
    // 这包括：中文字符、空格、标点符号等
    truncated = truncated.trim()
  }
  
  return truncated + suffix
}

/**
 * 智能截断文本（按字符数和行数双重限制）
 * @param {string} text - 要截断的文本
 * @param {number} maxChars - 每行最大字符数
 * @param {number} maxLines - 最大行数，默认为 2
 * @param {string} suffix - 截断后缀，默认为 '...'
 * @returns {string} 截断后的文本
 */
export const truncateTextMultiLine = (text, maxChars, maxLines = 2, suffix = '...') => {
  if (!text) return ''
  
  // 计算总长度限制
  const totalMaxLength = maxChars * maxLines
  
  // 如果文本长度不超过限制，直接返回
  if (text.length <= totalMaxLength) {
    return text
  }
  
  // 使用单行截断逻辑
  return truncateText(text, totalMaxLength, suffix)
}

/**
 * 判断文本是否需要截断
 * @param {string} text - 文本内容
 * @param {number} maxLength - 最大长度
 * @returns {boolean} 是否需要截断
 */
export const needsTruncate = (text, maxLength) => {
  return text && text.length > maxLength
}
